// AppInstallBanner.jsx
// Alttan kayan öneri kartı:
//  - Android tarayıcı  → Google Play'deki mobil uygulamamız
//  - Masaüstü Chromium (Chrome, Edge, Brave, Opera) → Chrome Web Mağazası'ndaki eklentimiz
// "Şimdi değil" 7 gün, mağazaya gitmek 30 gün boyunca kartı gizler (her öneri ayrı sayılır).
// Uygulamanın kendi içinde (WebView/TWA), PWA modunda, botlarda/prerender sırasında ve
// tahtanın göründüğü oyun sayfalarında gösterilmez.

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { logAnalyticsEvent } from '../firebase';
import { matchRoute, useLang } from '../i18n';
import { CHROME_EXTENSION_URL, PLAY_STORE_ID, PLAY_STORE_URL, withUtm } from '../config/links';
import './AppInstallBanner.css';

const DAY_MS = 24 * 60 * 60 * 1000;
const SHOW_DELAY_MS = 1500;
const HIDDEN_ROUTE_KEYS = new Set(['play', 'arena', 'puzzle']);

const KINDS = {
  app: { storageKey: 'sogi_app_banner_hidden_until', url: PLAY_STORE_URL, icon: '/android-icon-192x192.png' },
  ext: { storageKey: 'sogi_ext_banner_hidden_until', url: CHROME_EXTENSION_URL, icon: '/android-icon-192x192.png' },
};

function isBot() {
  const ua = navigator.userAgent || '';
  return navigator.webdriver || /Headless|bot|crawl|spider|Lighthouse/i.test(ua);
}

function detectKind() {
  if (isBot()) return null;
  const ua = navigator.userAgent || '';
  if (/Android/i.test(ua)) {
    if (/; wv\)/.test(ua)) return null; // uygulamanın kendi WebView'i
    if (document.referrer.startsWith('android-app://')) return null; // TWA
    if (window.matchMedia?.('(display-mode: standalone)').matches) return null; // PWA
    return 'app';
  }
  const isMobile = /iPhone|iPad|iPod|Mobile/i.test(ua);
  const isChromium = /Chrome\/|Edg\/|OPR\//.test(ua) && !/CriOS|FxiOS|SamsungBrowser/.test(ua);
  if (!isMobile && isChromium) return 'ext';
  return null;
}

function readHiddenUntil(key) {
  try {
    return Number(localStorage.getItem(key)) || 0;
  } catch {
    return 0;
  }
}

function hideFor(key, days) {
  try {
    localStorage.setItem(key, String(Date.now() + days * DAY_MS));
  } catch {
    // Gizli sekme vb. — sadece bu oturumda kapanır.
  }
}

export default function AppInstallBanner() {
  const { pathname } = useLocation();
  const { t } = useLang();
  const [kind, setKind] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const k = detectKind();
    if (!k || readHiddenUntil(KINDS[k].storageKey) > Date.now()) return undefined;

    let cancelled = false;
    const timer = setTimeout(async () => {
      if (k === 'app') {
        // Uygulama zaten yüklüyse gösterme. Bazı tarayıcılarda sorgu hiç dönmediği için
        // 1 sn'lik zaman aşımıyla yarıştırılır.
        try {
          const installed = await Promise.race([
            navigator.getInstalledRelatedApps?.(),
            new Promise((resolve) => setTimeout(resolve, 1000)),
          ]);
          if (installed?.some((app) => app.id === PLAY_STORE_ID)) return;
        } catch {
          // Desteklenmiyor — kartı göstermeye devam et.
        }
      }
      if (!cancelled) setKind(k);
    }, SHOW_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, []);

  const routeKey = matchRoute(pathname)?.key;
  const visible = kind && !dismissed && !HIDDEN_ROUTE_KEYS.has(routeKey);

  useEffect(() => {
    if (visible) logAnalyticsEvent(`${kind}_banner_view`, { page_path: pathname });
    // Sadece kart ilk göründüğünde say; sayfa değişimleri yeni gösterim değildir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  if (!visible) return null;

  const cfg = KINDS[kind];
  const title = t(kind === 'app' ? 'banner.appTitle' : 'banner.extTitle');
  const text = t(kind === 'app' ? 'banner.appText' : 'banner.extText');
  const cta = t(kind === 'app' ? 'banner.appCta' : 'banner.extCta');

  const handleInstall = () => {
    logAnalyticsEvent(`${kind}_banner_click`, { page_path: pathname });
    hideFor(cfg.storageKey, 30);
    setDismissed(true);
  };

  const handleDismiss = () => {
    logAnalyticsEvent(`${kind}_banner_dismiss`, { page_path: pathname });
    hideFor(cfg.storageKey, 7);
    setDismissed(true);
  };

  return (
    <div
      className={`app-install-banner ${kind === 'ext' ? 'is-desktop' : ''}`}
      role="dialog"
      aria-labelledby="app-install-title"
    >
      <button type="button" className="app-install-close" aria-label={t('banner.close')} onClick={handleDismiss}>
        ×
      </button>
      <div className="app-install-body">
        <img src={cfg.icon} alt="" className="app-install-icon" width="56" height="56" />
        <div className="app-install-text">
          <strong id="app-install-title">{title}</strong>
          <span>{text}</span>
        </div>
      </div>
      <div className="app-install-actions">
        <button type="button" className="btn btn-link app-install-later" onClick={handleDismiss}>
          {t('banner.later')}
        </button>
        <a
          href={withUtm(cfg.url, 'install_banner')}
          target="_blank"
          rel="noopener noreferrer"
          className="btn app-install-cta"
          onClick={handleInstall}
        >
          {cta}
        </a>
      </div>
    </div>
  );
}
